
import React, { useEffect, useRef, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CTAButton from '@/components/CTAButton';
import { SectionContainer } from '@/components/section/SectionContainer';
import { ArrowRight, Play, Pause, CheckCircle } from 'lucide-react';
import { useBreakpoint } from '@/hooks/use-mobile';
import { toast } from 'sonner';

const CampaignLanding = () => {
  const { isMobile } = useBreakpoint();
  const videoRef = useRef<HTMLIFrameElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasWatched, setHasWatched] = useState(false);
  
  // Video control functions
  const toggleVideo = () => {
    if (!videoRef.current?.contentWindow) return;
    
    const message = isPlaying ? 'pause' : 'play';
    videoRef.current.contentWindow.postMessage(message, '*');
    setIsPlaying(!isPlaying);
    
    // If user starts playing, mark as watched after 30 seconds
    if (!isPlaying && !hasWatched) {
      setTimeout(() => {
        setHasWatched(true);
        toast("Great! Now try AskSpot for yourself.", {
          action: {
            label: "Start Free Trial",
            onClick: () => window.location.href = '/pricing'
          }
        });
      }, 30000);
    }
  };

  // Monitor video messages (play/pause)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://www.loom.com') return;
      
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'play') {
          setIsPlaying(true);
        } else if (data.type === 'pause') {
          setIsPlaying(false);
        }
      } catch (e) {
        // Ignore parse errors
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      
      {/* Hero Section with Video */}
      <section className="pt-20 pb-12 md:pt-24 md:pb-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#003a70]/5 to-transparent z-0"></div>
        <SectionContainer>
          <div className="mx-auto max-w-4xl text-center mb-8 md:mb-12">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-[#FEE6D8] text-[#003a70] font-medium px-5 py-2.5 text-sm mb-6">
              <span>✨ AskSpot AI Solution ✨</span>
            </div>
            
            <h1 className="text-3xl md:text-5xl font-bold text-[#003a70] mb-4 md:mb-6">
              Build AI Solutions in Minutes — 
              <span className="text-[#003a70] relative">
                <span className="relative z-10">Without Coding</span>
                <span className="absolute bottom-1 left-0 right-0 h-3 bg-[#FEE6D8]/50 -z-10"></span>
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
              Create powerful AI assistants, chatbots, and interactive forms for your business—all without writing a single line of code.
            </p>
          </div>
          
          {/* Video Player */}
          <div className="relative max-w-3xl mx-auto rounded-xl overflow-hidden shadow-2xl">
            <div style={{ paddingBottom: '56.25%', height: 0, position: 'relative' }}>
              <iframe
                ref={videoRef}
                src="https://www.loom.com/embed/7fc7fe66a62c4ff09d60d3b39835addd?sid=900c711c-edc0-4787-843d-6941452244c3&hide_share=true&hideEmbedTopBar=true"
                frameBorder="0"
                allowFullScreen
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
              ></iframe>
              
              {/* Play/Pause Button */}
              <button
                onClick={toggleVideo}
                className="absolute bottom-4 right-4 bg-[#003a70]/70 hover:bg-[#003a70] text-white rounded-full p-3 transition-colors z-20"
                aria-label={isPlaying ? "Pause video" : "Play video"}
              >
                {isPlaying ? <Pause size={20} /> : <Play size={20} />}
              </button>
            </div>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
            <CTAButton to="/pricing" variant="primary" className="group">
              <span className="flex items-center">
                Start Your Free Trial
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </CTAButton>
            
            <CTAButton to="/book-demo" variant="accent">
              <span className="flex items-center">
                Book a Live Demo
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </CTAButton>
          </div>
        </SectionContainer>
      </section>
      
      {/* Key Benefits Section */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-white to-[#FEE6D8]/20">
        <SectionContainer>
          <h2 className="text-2xl md:text-3xl font-bold text-center text-[#003a70] mb-12">
            Why Businesses Choose AskSpot
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Save Time",
                description: "Build in minutes what would take developers weeks to create.",
                icon: "⏱️"
              },
              {
                title: "Increase Conversions",
                description: "Interactive forms and assistants boost engagement by 300%.",
                icon: "📈"
              },
              {
                title: "No Coding Required",
                description: "Our visual builder makes creation intuitive for everyone.",
                icon: "🚀"
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <div className="text-4xl mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-bold text-[#003a70] mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </SectionContainer>
      </section>
      
      {/* Social Proof Section */}
      <section className="py-12 md:py-16 bg-[#003a70]/5">
        <SectionContainer>
          <h2 className="text-2xl md:text-3xl font-bold text-center text-[#003a70] mb-4">
            Trusted by Innovative Teams
          </h2>
          <p className="text-center text-gray-600 mb-10 max-w-3xl mx-auto">
            From small businesses to large enterprises, teams are using AskSpot to transform how they collect data and interact with customers.
          </p>
          
          <div className="bg-white p-6 md:p-8 rounded-xl shadow-md max-w-3xl mx-auto">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-[#FEE6D8] flex items-center justify-center text-2xl">
                JD
              </div>
              <div>
                <h3 className="text-lg font-bold">Jane Doe</h3>
                <p className="text-sm text-gray-500">Marketing Director, TechCorp</p>
              </div>
            </div>
            <blockquote className="text-lg text-gray-700 italic">
              "We increased our lead conversion rate by 45% after implementing AskSpot's interactive forms. The setup was incredibly easy, and we were live in less than a day."
            </blockquote>
          </div>
        </SectionContainer>
      </section>
      
      {/* Features Overview */}
      <section className="py-12 md:py-16">
        <SectionContainer>
          <h2 className="text-2xl md:text-3xl font-bold text-center text-[#003a70] mb-12">
            Everything You Need to Succeed
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                title: "AI Assistants",
                description: "Create customized AI chatbots that answer questions, qualify leads, and provide support 24/7."
              },
              {
                title: "Smart Forms",
                description: "Build interactive forms that adapt based on user responses, increasing completion rates."
              },
              {
                title: "Visual Flow Builder",
                description: "Design complex workflows with our drag-and-drop interface—no technical skills required."
              },
              {
                title: "Analytics Dashboard",
                description: "Gain insights into user interactions and optimize your forms and assistants."
              }
            ].map((feature, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-[#003a70]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#003a70] mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </SectionContainer>
      </section>
      
      {/* Final CTA Section */}
      <section className="py-12 md:py-16 bg-[#FEE6D8]/30">
        <SectionContainer>
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-bold text-[#003a70] mb-4">
              Ready to Transform Your Business?
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              Join thousands of businesses already using AskSpot to create powerful AI solutions without coding.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <CTAButton to="/pricing" variant="primary" className="group">
                <span className="flex items-center">
                  Start Your 30-Day Free Trial
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </CTAButton>
              
              <CTAButton to="/book-demo" variant="accent">
                <span className="flex items-center">
                  Schedule a Demo
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </CTAButton>
            </div>
          </div>
        </SectionContainer>
      </section>
      
      <Footer />
    </div>
  );
};

export default CampaignLanding;
